import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Slider, Tooltip, SvgIcon, Fade } from "@mui/material";
import { debounce } from "lodash-es";
import { Close as CloseIcon } from "@mui/icons-material";
import styled, { CSSProperties } from "styled-components";

import { ReactComponent as AdjustIcon } from "src/assets/icons/adjust.svg";
import { Overlayer } from "src/types";
import { layerName } from "src/utils/overlayer.utils";

enum Mode {
  default,
  transparency,
}

interface Props {
  draggableProps: any;
  dragHandleProps: any;
  overlayer: Overlayer;
  onChange: (overlayer: Overlayer) => void;
  style: CSSProperties;
}

export const ActiveLayerItem = forwardRef<any, Props>((props, ref) => {
  const { overlayer } = props;
  const [mode, setMode] = useState<Mode>(Mode.default);

  return render();

  function render() {
    return (
      <Styles ref={ref} {...props.draggableProps} style={props.style}>
        <Fade in={mode === Mode.default}>
          <DefaultMode overlayer={overlayer} onModeChange={setMode} dragHandleProps={props.dragHandleProps} />
        </Fade>

        <Fade in={mode === Mode.transparency}>
          <TransparencyMode
            overlayer={overlayer}
            onChange={props.onChange}
            onClose={() => setMode(Mode.default)}
            className="action-container"
          />
        </Fade>
      </Styles>
    );
  }
});

const Styles = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  height: 50px;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0, 2)};
  align-items: center;

  .action-container {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const DefaultMode = forwardRef<
  HTMLDivElement,
  {
    className?: string;
    overlayer: Overlayer;
    dragHandleProps: any;
    onModeChange: (mode: Mode) => void;
    style?: any;
  }
>((props, ref) => {
  const { overlayer } = props;
  const { t } = useTranslation();
  return render();

  function render() {
    return (
      <DefaultModeStyles style={props.style} ref={ref} {...props.dragHandleProps}>
        <div className="name">{layerName(overlayer, t)}</div>

        <IconButton
          aria-label="transparency"
          color="default"
          onClick={() => props.onModeChange(Mode.transparency)}
          size="large"
        >
          <SvgIcon component={AdjustIcon} viewBox="0 0 512 512" fontSize="small" className="btn-action" />
        </IconButton>
      </DefaultModeStyles>
    );
  }
});

const DefaultModeStyles = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  width: 100%;

  .name {
    flex: 1 1 100%;
    color: ${({ theme }) => theme.palette.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .btn-action {
    font-size: 1rem;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const TransparencyMode = forwardRef<
  HTMLDivElement,
  {
    className?: string;
    overlayer: Overlayer;
    onChange: (layer: Overlayer) => void;
    onClose: () => void;
    style?: any;
  }
>((props, ref) => {
  const { overlayer } = props;
  return render();

  function render() {
    return (
      <TransparencyModeStyles className={props.className} style={props.style} ref={ref}>
        <Slider
          value={overlayer.opacity * 100}
          step={10}
          min={0}
          max={100}
          size="small"
          valueLabelDisplay="auto"
          onChange={debounce((event, newValue) => {
            event.preventDefault();
            event.stopPropagation();
            if (!Array.isArray(newValue)) props.onChange({ ...overlayer, opacity: newValue / 100 });
          })}
          components={{ ValueLabel: ValueLabelComponent }}
        />
        <IconButton aria-label="transparency" color="default" onClick={props.onClose} size="large">
          <CloseIcon fontSize="small" />
        </IconButton>
      </TransparencyModeStyles>
    );
  }
});

const TransparencyModeStyles = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2, 2)};
  gap: ${({ theme }) => theme.spacing(2, 2)};
  width: 100%;
`;

interface ValueLabelComponentProps {
  children: React.ReactElement;
  open: boolean;
  value: number;
}

function ValueLabelComponent(props: ValueLabelComponentProps) {
  const { children, open, value } = props;

  return (
    <Tooltip open={open} enterTouchDelay={0} placement="top" title={value}>
      {children}
    </Tooltip>
  );
}
